import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dropdown } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import Select from 'react-select'
import XLSX from 'xlsx'

import FilterSearch from 'pages/parts/FilterSearch'

import { httpOk, GET } from 'helpers/api'
import { findKeyFromArrayByValue, formatMoney, returnSign, formatDate } from 'helpers/helpers'
import CustomTooltip from "pages/parts/CustomTooltip"

function GrossIncome() {
	const { t } = useTranslation()
	const date = new Date()
	const reduxAccount = useSelector(state => state.account)

	const [data, setData] = useState({
		'data': []
	})
	const [rolls, setRolls] = useState([])
	const [productTypes, setProductTypes] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [rowFilter, setRowFilter] = useState('all') // 'all' | 'loss' | 'pending'
	const [filterData, setFilterData] = useState({
		'start_date': formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		'end_date': formatDate(new Date(), 'yyyy-MM-dd'),
		'pos_id': reduxAccount.pos_id,
		'product_type_id': '',
		'search': '',
	})

	async function searchWithFilter(exportToExcel = false, tableSort = "") {
		var filterUrl = "/report/gross-income"
		var urlParams = ""
		checkFilter()

		if (filterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterData.pos_id
		if (filterData.roll_id)
			urlParams += returnSign(urlParams) + 'roll_id=' + filterData.roll_id.product_id
		if (filterData.order_type)
			urlParams += returnSign(urlParams) + 'order_type=' + filterData.order_type
		if (filterData.product_type_id)
			urlParams += returnSign(urlParams) + 'product_type_id=' + filterData.product_type_id
		if (filterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterData.start_date
		if (filterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterData.end_date
		if (filterData.search)
			urlParams += returnSign(urlParams) + 'search=' + encodeURIComponent(filterData.search)

		if (`/report/gross-income${urlParams}` !== filterUrl && !exportToExcel)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		if (exportToExcel) {
			var excelHeaders = [[t('name2'), t('quantity'), t('price'), t('sale_price'), t('gross_income'), '%', t('markup'), t('margin')]]

			for (let i = 0; i < data?.data?.length; i++) {
				var excelItems = []
				excelItems.push(data.data[i]['name'])
				excelItems.push(data.data[i]['quantity'])
				excelItems.push(data.data[i]['price'])
				excelItems.push(data.data[i]['sale_price'])
				excelItems.push(data.data[i]['income'])
				excelItems.push(data.data[i]['percent'])
				excelItems.push(calcMarkup(data.data[i]['gross_income'], data.data[i]['total_cost_price']).toFixed(1))
				excelItems.push(calcMargin(data.data[i]['gross_income'], data.data[i]['delivered_sales']).toFixed(1))
				excelHeaders.push(excelItems)
			}

			const ws = XLSX.utils.aoa_to_sheet(excelHeaders);
			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
			XLSX.writeFile(wb, t('gross_income') + ".xlsx");
		} else {
			const response = await GET(filterUrl, {}, { loader: true })
			if (httpOk(response)) setData(response.data)
		}

		setDropdownIsOpen(false)
	}

	function resetFilter() {
		setFilterData({
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			pos_id: null,
			product_type_id: '',
			roll_id: null,
			search: '',
		})
		searchWithFilter()
		setFilterDataExist(false)
	}

	function checkFilter() {
		if (
			filterData.pos_id === null &&
			filterData.start_date === null &&
			filterData.end_date === null &&
			(filterData.product_type_id === '' || filterData.product_type_id === null) &&
			filterData.roll_id === null &&
			filterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	// Натценка (ustama) = yalpi daromad / tannarx × 100
	function calcMarkup(income, cost) {
		const c = Number(cost || 0)
		if (!c) return 0
		return (Number(income || 0) / c) * 100
	}

	// Маржа = yalpi daromad / sotuv summasi × 100
	function calcMargin(income, sales) {
		const s = Number(sales || 0)
		if (!s) return 0
		return (Number(income || 0) / s) * 100
	}

	async function getMenu() {
		const response = await GET('/helper/rolls', {}, { loader: true })
		if (httpOk(response)) setRolls(response.data)
	}

	async function getProductTypes() {
		const response = await GET('/helper/products-types')
		if (httpOk(response)) setProductTypes(response.data)
	}

	useEffect(() => {
		getMenu()
		getProductTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		searchWithFilter(false)
	}, [filterData.search]) // eslint-disable-line react-hooks/exhaustive-deps

	// Qatorlar bo'yicha filtr (client-side): zarar (foyda < 0) yoki Kutilmoqda (yetkazilmagan qism > 0)
	const rows = (data?.data || []).filter(item => {
		if (rowFilter === 'loss') return Number(item.gross_income) < 0
		if (rowFilter === 'pending') return Number(item.pending_quantity) > 0
		return true
	})
	// tfoot jamlanmasi KO'RINAYOTGAN qatorlardan hisoblanadi (filtrga mos)
	const sumRows = (key) => rows.reduce((acc, r) => acc + Number(r[key] || 0), 0)
	const footRealizedSales = rows.reduce((acc, r) => acc + Number(r.delivered_sales || 0) + Number(r.fulfilled_sales || 0), 0)
	const footGross = sumRows('gross_income')
	const footCost = sumRows('total_cost_price')

	// Ўртача таннарх — HAQIQIY: tannarx summasi ÷ yetkazilgan (realized) miqdor.
	// Backorder yetib kelmagan (pending) qismning tannarxi hali yo'q, shuning uchun uni maxrajga qo'shmaymiz.
	const realAvgCost = (item) => {
		const realizedQty = Number(item.total_quantity || 0) - Number(item.pending_quantity || 0)
		if (realizedQty > 0) return Number(item.total_cost_price || 0) / realizedQty
		return Number(item.avg_price || 0)
	}

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('gross_income')} [{rows.length}]
					{rowFilter !== 'all' &&
						<span
							className={`badge ms-2 ${rowFilter === 'loss' ? 'bg-danger' : 'bg-warning text-dark'}`}
							style={{ cursor: 'pointer', fontSize: '0.6em' }}
							title={t('reset')}
							onClick={() => setRowFilter('all')}>
							{t('show')}: {rowFilter === 'loss' ? t('loss') : t('expected')} ✕
						</span>
					}
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									filterData={filterData}
									setFilterData={setFilterData}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('roll')}</label>
												<Select
													isClearable
													options={rolls}
													value={filterData.roll_id}
													onChange={(option) => setFilterData({ ...filterData, 'roll_id': option || null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.product_id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('type')}</label>
												<Select
													isClearable
													options={productTypes}
													value={productTypes.find(option => option.id === filterData.product_type_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'product_type_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('period')}</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.start_date ? new Date(filterData.start_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'start_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
											<div className="form-group col-md-6">
												<label className="invisible">.</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.end_date ? new Date(filterData.end_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'end_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row mt-2">
											<div className="form-group col-md-12">
												<label>{t('show')}</label>
												<div className="btn-group w-100" role="group">
													<button type="button" className={`btn ${rowFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setRowFilter('all')}>{t('all')}</button>
													<button type="button" className={`btn ${rowFilter === 'loss' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => setRowFilter('loss')}>{t('loss')}</button>
													<button type="button" className={`btn ${rowFilter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => setRowFilter('pending')}>{t('expected')}</button>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter(false)}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2" onClick={() => searchWithFilter(true)}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
						</div>
						<div className="vertical-center" style={{ 'width': '300px' }}>
							<h6 className="d-flex justify-content-between">
								<b>{t('sales')}:</b>
								<span>{formatMoney(data?.total_sales)}</span>
							</h6>
							{Number(data?.total_pending_sales) > 0 &&
								<h6 className="d-flex justify-content-between text-warning">
									<b>{t('expected_sales')}:</b>
									<span>{formatMoney(data?.total_pending_sales)}</span>
								</h6>
							}
							<h6 className="d-flex justify-content-between">
								<b>{t('cost_price')}:</b>
								<span>{formatMoney(data?.total_cost)}</span>
							</h6>
							<h6 className="d-flex justify-content-between">
								<b>{t('gross_income')}:</b>
								<span>{formatMoney(data?.total_gross_income)}</span>
							</h6>
							{Number(data?.total_fulfilled_profit) !== 0 &&
								<h6 className="d-flex justify-content-between" title={t('pending_profit_hint')}>
									<b>{t('realized_deferred_profit')}:</b>
									<span className={Number(data?.total_fulfilled_profit) < 0 ? 'text-danger' : ''}>{formatMoney(data?.total_fulfilled_profit)}</span>
								</h6>
							}
							<h6 className="d-flex justify-content-between">
								<b>{t('markup')}:</b>
								<span>{calcMarkup(data?.total_gross_income, data?.total_cost).toFixed(1)}%</span>
							</h6>
							<h6 className="d-flex justify-content-between">
								<b>{t('margin')}:</b>
								<span>{calcMargin(data?.total_gross_income, Number(data?.total_delivered_sales) + Number(data?.total_fulfilled_sales || 0)).toFixed(1)}%</span>
							</h6>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date &&
											<span>{formatDate(filterData.start_date)}</span>
										}
										{(filterData.start_date && filterData.end_date) &&
											<span>|</span>
										}
										{filterData.end_date &&
											<span>{formatDate(filterData.end_date)}</span>
										}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
								{filterData.product_type_id &&
									<span className="me-2">
										<b>{t('type')}: </b>
										{findKeyFromArrayByValue(productTypes, 'id', filterData.product_type_id, 'name')}
									</span>
								}
								{filterData.roll_id &&
									<span className="me-2">
										<b>{t('roll')}: </b>
										{filterData.roll_id.name}
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive table-fixed mb-3" style={{ minHeight: 'calc(100vh - 225px)' }}>
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('name2')}</th>
									<th style={{ 'width': '10%' }}>{t('type')}</th>
									<th style={{ 'width': '30%' }}>{t('roll')}</th>
									<th style={{ 'minWidth': '120px' }} className="td-ellipsis text-end">{t('quantity')}</th>
									<th style={{ 'minWidth': '120px' }} className="td-ellipsis text-end">{t('sq_m')}</th>
									<th style={{ 'minWidth': '150px' }} className="td-ellipsis text-end" title={t('avg_cost_price')}>{t('avg_cost_price')}</th>
									<th style={{ 'minWidth': '150px' }} className="td-ellipsis text-end" title={t('avg_sale_price')}>{t('avg_sale_price')}</th>
									<th style={{ 'minWidth': '150px' }} className="td-ellipsis text-end" title={t('cost_price_amount')}>{t('cost_price_amount')}</th>
									<th style={{ 'minWidth': '150px' }} className="td-ellipsis text-end" title={t('sales_amount')}>{t('sales_amount')}</th>
									<th style={{ 'minWidth': '150px' }} className="td-ellipsis text-end" title={t('gross_income')}>{t('gross_income')}</th>
									<th style={{ 'minWidth': '110px' }} className="td-ellipsis text-end" title={t('markup')}>{t('markup')}</th>
									<th style={{ 'minWidth': '110px' }} className="td-ellipsis text-end" title={t('margin')}>{t('margin')}</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((item, index) => (
									<tr key={index} className={Number(item.gross_income) < 0 ? 'table-danger' : ''}>
										<td className="text-center">{index + 1}</td>
										<td>{item.name}</td>
										<td>{item.product_type_name}</td>
										<td>{item.element_name}</td>
										<td className="text-end">{formatMoney(item.total_quantity, 3)}</td>
										<td className="text-end">{formatMoney(item.total_kv_m, 3)}</td>
										<td className="text-end">{formatMoney(realAvgCost(item))}</td>
										<td className="text-end">{formatMoney(item.avg_sale_price)}</td>
										<td className="text-end">{formatMoney(item.total_cost_price)}</td>
										<td className="text-end">{formatMoney(item.total_sales_price)}</td>
										<td className={`text-end ${Number(item.gross_income) < 0 ? 'text-danger fw-bold' : ''}`}>
											{formatMoney(item.gross_income)}
											{Number(item.pending_quantity) > 0 &&
												<span className="badge bg-warning text-dark ms-1" title={t('pending_profit_hint')}>{t('expected')} {formatMoney(item.pending_sales)}</span>
											}
										</td>
										<td className={`text-end ${Number(item.gross_income) < 0 ? 'text-danger' : ''}`}>{calcMarkup(item.gross_income, item.total_cost_price).toFixed(1)}%</td>
										<td className={`text-end ${Number(item.gross_income) < 0 ? 'text-danger' : ''}`}>{calcMargin(item.gross_income, Number(item.delivered_sales) + Number(item.fulfilled_sales || 0)).toFixed(1)}%</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									<td className="text-end"><b>{formatMoney(sumRows('total_quantity'), 3)}</b></td>
									<td className="text-end"><b>{formatMoney(sumRows('total_kv_m'), 3)}</b></td>
									<td></td>
									<td></td>
									<td className="text-end"><b>{formatMoney(footCost)}</b></td>
									<td className="text-end"><b>{formatMoney(sumRows('total_sales_price'))}</b></td>
									<td className="text-end"><b>{formatMoney(footGross)}</b></td>
									<td className="text-end"><b>{calcMarkup(footGross, footCost).toFixed(1)}%</b></td>
									<td className="text-end"><b>{calcMargin(footGross, footRealizedSales).toFixed(1)}%</b></td>
								</tr>
							</tfoot>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default GrossIncome